interface ResourceNode {
	x: number
	y: number
	r: number
	label: string
	type: 'topic' | 'resource' | 'ext'
}

export function ResourceVisual({ idPrefix }: { idPrefix: string }) {
	const nodes: ResourceNode[] = [
		{ x: 280, y: 214, r: 14, label: 'Tema', type: 'topic' },
		// Curated resources (lit when their line arrives)
		{ x: 150, y: 112, r: 8, label: 'CTE', type: 'resource' },
		{ x: 418, y: 102, r: 8, label: 'Guía COAM', type: 'resource' },
		{ x: 116, y: 252, r: 7, label: 'Modelo contrato', type: 'resource' },
		{ x: 446, y: 234, r: 7, label: 'LOE', type: 'resource' },
		{ x: 218, y: 326, r: 6, label: 'ArchDaily', type: 'resource' },
		{ x: 354, y: 322, r: 6, label: 'Tectónica', type: 'resource' },
		// Background mesh — static, very muted, never lit
		{ x: 70, y: 170, r: 4, label: '', type: 'ext' },
		{ x: 488, y: 152, r: 4, label: '', type: 'ext' },
		{ x: 188, y: 358, r: 4, label: '', type: 'ext' },
		{ x: 408, y: 358, r: 4, label: '', type: 'ext' },
		{ x: 76, y: 308, r: 3, label: '', type: 'ext' },
		{ x: 482, y: 304, r: 3, label: '', type: 'ext' },
		{ x: 280, y: 78, r: 3, label: '', type: 'ext' },
	]

	const topic = nodes[0]!
	const resourceIndices = nodes
		.map((n, i) => (n.type === 'resource' ? i : -1))
		.filter((i) => i >= 0)

	// Background mesh edges — static, faint, give the sense of a wider graph
	// without being noisy. They don't animate.
	const meshEdges: [number, number][] = [
		[1, 7], // CTE → ext
		[2, 8], // COAM → ext
		[3, 11], // Contrato → ext
		[4, 12], // LOE → ext
		[5, 9], // ArchDaily → ext
		[6, 10], // Tectónica → ext
		[1, 13], // CTE → top ext
		[2, 13], // COAM → top ext
		[1, 3], // CTE ↔ Contrato
		[2, 4], // COAM ↔ LOE
		[5, 6], // ArchDaily ↔ Tectónica
	]

	const glowId = `${idPrefix}-resource-glow`

	// Pseudo-random delays so lines draw in irregularly. Same line lengths
	// converge at slightly different times, which reads as organic.
	const drawDelay = (resourceListIndex: number) => {
		const seed = (resourceListIndex * 131 + 47) % 100
		return (seed / 100) * 1.6
	}
	const drawDuration = (resourceListIndex: number) => {
		const seed = (resourceListIndex * 53 + 19) % 100
		return 0.7 + (seed / 100) * 0.6
	}

	return (
		<svg
			viewBox="0 0 560 420"
			className="h-full w-full"
			role="img"
			aria-label="Recursos curados vinculados al tema"
			style={{ color: 'var(--gold-text)' }}
		>
			<defs>
				<filter id={glowId}>
					<feGaussianBlur
						in="SourceGraphic"
						stdDeviation="5"
						result="blurred"
					/>
					<feMerge>
						<feMergeNode in="blurred" />
						<feMergeNode in="SourceGraphic" />
					</feMerge>
				</filter>
			</defs>

			<text
				x="280"
				y="30"
				textAnchor="middle"
				fontSize="12"
				fill="rgba(255,255,255,0.42)"
				fontFamily="ui-monospace, SFMono-Regular, monospace"
				letterSpacing="2"
			>
				RECURSOS · FUENTES VINCULADAS AL TEMA
			</text>

			{/* Background mesh edges — static, very faint. */}
			{meshEdges.map(([from, to], index) => {
				const a = nodes[from]!
				const b = nodes[to]!
				return (
					<line
						key={`mesh-${from}-${to}-${index}`}
						x1={a.x}
						y1={a.y}
						x2={b.x}
						y2={b.y}
						stroke="currentColor"
						strokeWidth="0.4"
						strokeOpacity="0.07"
					/>
				)
			})}

			{/* Background mesh nodes — never lit. */}
			{nodes
				.map((node, index) => ({ node, index }))
				.filter(({ node }) => node.type === 'ext')
				.map(({ node, index }) => (
					<circle
						key={`ext-${index}`}
						cx={node.x}
						cy={node.y}
						r={node.r}
						fill="white"
						fillOpacity="0.04"
						stroke="white"
						strokeWidth="0.5"
						strokeOpacity="0.1"
					/>
				))}

			{/* Lines from topic to each resource — drawn irregularly, stay lit. */}
			{resourceIndices.map((nodeIndex, listIndex) => {
				const node = nodes[nodeIndex]!
				const dx = node.x - topic.x
				const dy = node.y - topic.y
				const length = Math.sqrt(dx * dx + dy * dy)
				const delay = drawDelay(listIndex)
				const duration = drawDuration(listIndex)

				return (
					<line
						key={`line-${nodeIndex}`}
						x1={topic.x}
						y1={topic.y}
						x2={node.x}
						y2={node.y}
						stroke="currentColor"
						strokeWidth="1"
						strokeOpacity="0.32"
						strokeLinecap="round"
						strokeDasharray={length}
						strokeDashoffset={length}
					>
						<animate
							attributeName="stroke-dashoffset"
							from={length}
							to="0"
							dur={`${duration}s`}
							begin={`${delay}s`}
							fill="freeze"
							calcMode="spline"
							keySplines="0.4 0 0.2 1"
						/>
					</line>
				)
			})}

			{/* Resource nodes — start muted, brighten when their line arrives. */}
			{resourceIndices.map((nodeIndex, listIndex) => {
				const node = nodes[nodeIndex]!
				const delay = drawDelay(listIndex)
				const duration = drawDuration(listIndex)
				const litTime = delay + duration

				return (
					<g key={`node-${nodeIndex}`}>
						{/* Halo, fades in after line arrives. */}
						<circle
							cx={node.x}
							cy={node.y}
							r={node.r + 4}
							fill="currentColor"
							fillOpacity="0"
						>
							<animate
								attributeName="fillOpacity"
								from="0"
								to="0.07"
								dur="0.4s"
								begin={`${litTime}s`}
								fill="freeze"
							/>
						</circle>
						{/* Outer ring — starts dim, brightens. */}
						<circle
							cx={node.x}
							cy={node.y}
							r={node.r}
							fill="currentColor"
							fillOpacity="0.04"
							stroke="currentColor"
							strokeWidth="1"
							strokeOpacity="0.14"
						>
							<animate
								attributeName="fillOpacity"
								from="0.04"
								to="0.14"
								dur="0.45s"
								begin={`${litTime}s`}
								fill="freeze"
							/>
							<animate
								attributeName="strokeOpacity"
								from="0.14"
								to="0.45"
								dur="0.45s"
								begin={`${litTime}s`}
								fill="freeze"
							/>
						</circle>
						{/* Inner dot — brightens last for a "switch on" feel. */}
						<circle
							cx={node.x}
							cy={node.y}
							r="2.5"
							fill="currentColor"
							fillOpacity="0.15"
						>
							<animate
								attributeName="fillOpacity"
								from="0.15"
								to="0.6"
								dur="0.35s"
								begin={`${litTime + 0.05}s`}
								fill="freeze"
							/>
						</circle>
					</g>
				)
			})}

			{/* Topic center — already lit on mount. */}
			<circle
				cx={topic.x}
				cy={topic.y}
				r={topic.r + 5}
				fill="currentColor"
				fillOpacity="0.06"
			/>
			<circle
				cx={topic.x}
				cy={topic.y}
				r={topic.r}
				fill="currentColor"
				fillOpacity="0.15"
				stroke="currentColor"
				strokeWidth="1.6"
				strokeOpacity="0.7"
				filter={`url(#${glowId})`}
			/>
			<circle
				cx={topic.x}
				cy={topic.y}
				r="5"
				fill="currentColor"
				fillOpacity="0.6"
			>
				<animate
					attributeName="r"
					values="5;6.5;5"
					dur="2.4s"
					repeatCount="indefinite"
				/>
			</circle>

			{/* Labels — fade in after their node lights. */}
			{resourceIndices.map((nodeIndex, listIndex) => {
				const node = nodes[nodeIndex]!
				const delay = drawDelay(listIndex)
				const duration = drawDuration(listIndex)
				const litTime = delay + duration

				return (
					<text
						key={`label-${nodeIndex}`}
						x={node.x}
						y={node.y + node.r + 16}
						textAnchor="middle"
						fontSize="12"
						fill="rgba(255,255,255,0.55)"
						opacity="0"
					>
						{node.label}
						<animate
							attributeName="opacity"
							from="0"
							to="1"
							dur="0.4s"
							begin={`${litTime}s`}
							fill="freeze"
						/>
					</text>
				)
			})}

			<text
				x={topic.x}
				y={topic.y + topic.r + 16}
				textAnchor="middle"
				fontSize="12"
				fill="rgba(255,255,255,0.78)"
			>
				Tema
			</text>

			<text
				x="280"
				y="398"
				textAnchor="middle"
				fontSize="12"
				fill="rgba(255,255,255,0.38)"
			>
				Fuentes externas curadas · trazables desde el tema
			</text>
		</svg>
	)
}
